import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, Backspace, SignIn, SignOut, WarningCircle } from "@phosphor-icons/react";

const stateLabel = { in: "Here now", out: "Signed out", absent: "Absent", none: "Not arrived" };

export default function PublicCheckin() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [child, setChild] = useState(null);
  const [kind, setKind] = useState("in");
  const [code, setCode] = useState("");
  const [guardian, setGuardian] = useState("");
  const [done, setDone] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/checkin/roster", { params: { token } });
      setData(d);
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || "This link is no longer valid.");
    }
  };

  useEffect(() => {
    if (!token) setError("This link is missing its sign-in code. Please scan the QR code again.");
    else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (full) => {
    try {
      const { data: r } = await api.post("/checkin", {
        token, student_id: child.id, code: full, kind, guardian,
      });
      setDone(`${r.student} is ${r.kind === "in" ? "signed in" : "signed out"}. Thank you!`);
      setChild(null);
      setCode("");
      setGuardian("");
      load();
      setTimeout(() => setDone(null), 5000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setCode("");
    }
  };

  const press = (d) => {
    const next = (code + d).slice(0, 4);
    setCode(next);
    if (next.length === 4) submit(next);
  };

  if (error)
    return (
      <div className="min-h-screen grid place-items-center bg-[#0F172A] text-white px-6">
        <div className="text-center max-w-sm" data-testid="checkin-error">
          <WarningCircle size={64} weight="fill" className="mx-auto text-[#FBBF24]" />
          <h1 className="mt-6 font-display text-3xl font-bold">Can't sign in here</h1>
          <p className="mt-3 text-white/70">{error}</p>
          <p className="mt-6 text-sm text-white/50">Please see the front desk, or call (501) 904-7103.</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0F172A] text-white px-5 py-8">
      <div className="mx-auto max-w-md">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FBBF24]">Quick Sign-In</p>
        <h1 className="mt-2 font-display text-3xl font-bold">
          {data?.program || "Teach Play to Learn"}
        </h1>

        {done && (
          <div className="mt-6 rounded-3xl bg-emerald-500/15 border border-emerald-400/40 p-5 flex items-center gap-4 tpl-fade-up"
               data-testid="checkin-success">
            <CheckCircle size={32} weight="fill" className="text-emerald-300 shrink-0" />
            <p className="font-bold">{done}</p>
          </div>
        )}

        {!data ? (
          <p className="mt-8 text-white/60">Loading…</p>
        ) : !child ? (
          <>
            <p className="mt-6 text-white/60">Tap your child's name.</p>
            <div className="mt-4 space-y-3" data-testid="checkin-roster">
              {data.students.map((s) => (
                <button key={s.id} onClick={() => { setChild(s); setKind(s.state === "in" ? "out" : "in"); }}
                        className="w-full text-left rounded-2xl bg-white/5 border border-white/15 p-5 active:scale-[0.98] hover:bg-white/10"
                        style={{ transition: "transform .15s ease, background-color .2s ease" }}
                        data-testid={`checkin-child-${s.id}`}>
                  <p className="font-display text-xl font-bold">{s.name}</p>
                  <p className="text-sm text-white/50 mt-1 font-semibold">
                    {s.room} · {stateLabel[s.state]}
                  </p>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="mt-6" data-testid="checkin-code-panel">
            <p className="text-white/60 font-semibold">Signing {kind === "in" ? "in" : "out"}</p>
            <p className="mt-1 font-display text-3xl font-bold">{child.name}</p>
            <div className="mt-4 flex gap-3">
              <Button onClick={() => setKind("in")}
                      className={`flex-1 h-12 rounded-full font-bold ${kind === "in" ? "bg-[#10B981] hover:bg-emerald-600" : "bg-white/10 hover:bg-white/20"}`}
                      data-testid="checkin-kind-in"><SignIn size={18} className="mr-2" /> In</Button>
              <Button onClick={() => setKind("out")}
                      className={`flex-1 h-12 rounded-full font-bold ${kind === "out" ? "bg-[#3B82F6] hover:bg-[#2563EB]" : "bg-white/10 hover:bg-white/20"}`}
                      data-testid="checkin-kind-out"><SignOut size={18} className="mr-2" /> Out</Button>
            </div>
            <Input value={guardian} onChange={(e) => setGuardian(e.target.value)}
                   placeholder="Your name (for the sign-in sheet)"
                   className="mt-4 h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/40"
                   data-testid="checkin-guardian" />

            <p className="mt-6 text-white/60 font-semibold">Enter your 4-digit code</p>
            <div className="mt-3 flex gap-3" data-testid="checkin-code-dots">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className={`h-14 flex-1 rounded-2xl border-2 grid place-items-center font-display text-2xl font-bold ${
                  code.length > i ? "border-[#FBBF24] bg-white/10" : "border-white/20"}`}>
                  {code.length > i ? "•" : ""}
                </span>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <button key={d} onClick={() => press(String(d))}
                        className="h-16 rounded-2xl bg-white/5 border border-white/15 font-display text-2xl font-bold active:scale-95 hover:bg-white/15"
                        style={{ transition: "transform .15s ease, background-color .2s ease" }}
                        data-testid={`checkin-key-${d}`}>{d}</button>
              ))}
              <button onClick={() => setCode("")}
                      className="h-16 rounded-2xl bg-white/5 border border-white/15 grid place-items-center hover:bg-white/15"
                      data-testid="checkin-key-clear"><Backspace size={24} /></button>
              <button onClick={() => press("0")}
                      className="h-16 rounded-2xl bg-white/5 border border-white/15 font-display text-2xl font-bold active:scale-95 hover:bg-white/15"
                      style={{ transition: "transform .15s ease, background-color .2s ease" }}
                      data-testid="checkin-key-0">0</button>
            </div>
            <Button variant="ghost" onClick={() => { setChild(null); setCode(""); }}
                    className="mt-5 w-full rounded-full text-white/60 hover:text-white hover:bg-white/10 font-bold"
                    data-testid="checkin-cancel-btn">Choose a different child</Button>
          </div>
        )}
      </div>
    </div>
  );
}
